import { parseISO, format, isSameDay } from 'date-fns';

import {
  EMAIL,
  EmbeddedMessage,
  IN_APP,
  MESSAGE_MEDIUMS_WITH_WHATS_APP,
} from 'constants/messageMediums';
import { Locale, TemplateCreatorUser, TemplateUpdatedByUser } from 'modules/domains/template';
import {
  MetaApprovalStatusDefinition,
  MetaQualityRating,
  PseudoApprovalStatusDefinition,
  TemplateTypeDefinition,
} from 'modules/graphql/types/GraphQLTemplate';
import { Nullable } from 'utils/tsHelpers';
import { PREVIEW_HTML_TEMPLATE_URL, PREVIEW_NON_HTML_TEMPLATE_URL } from '../constants';

/**
 * Given a numeric identifier of the message medium, find the associated label
 */
export const getLabelByMedium = (medium: string) =>
  Object.values(MESSAGE_MEDIUMS_WITH_WHATS_APP).find((msgMed) => msgMed.dbKey === medium)?.label ||
  (medium === EmbeddedMessage && 'Embedded');

/**
 * Given a string representing a date and time, return a readable display value
 * Returns "Today" instead of the date if the date provided matches the referenceDate
 */
export const getDisplayDate = (
  date: string,
  referenceDate: string = new Date().toISOString(),
): string => {
  const parsedDate = parseISO(date);
  const parsedTime = format(parsedDate, 'h:mm a'); // Format time without leading zero
  const isToday = isSameDay(parsedDate, parseISO(referenceDate));

  if (isToday) {
    return `Today, ${parsedTime}`;
  }
  // Format date as MM/DD/YY (with abbreviated year)
  const formattedDate = format(parsedDate, 'M/d/yy');
  return `${formattedDate}, ${parsedTime}`;
};

/** Gets the user name and avatar url to be displayed for the template avatar */
export const getAvatarDisplayInformation = ({
  creatorUser,
  updatedByUser,
}: TemplateCreatorUser & { updatedByUser: Nullable<TemplateUpdatedByUser['updatedByUser']> }) => {
  const userInfo = updatedByUser || creatorUser || {};
  const name = userInfo?.fullName || '';
  const firstName = name.split(' ')[0];
  const lastName = name.split(' ').reverse()[0];
  const avatarDisplayInfo = { firstName, lastName };
  return { ...avatarDisplayInfo, imgSrc: userInfo?.avatarUrl || undefined };
};

/** Returns the number of selections that match the filter id's provided for a filter group */
export const getTotalSelectionsByFilterGroup = (filterIdMap: string[], selections: string[]) =>
  filterIdMap.reduce((total, filter) => (selections.includes(filter) ? total + 1 : total), 0);

/** Returns string to display as label for locale in filter menu */
export const getLocaleLabel = (locale: Locale) => {
  if (locale?.languageName?.length) {
    return `${locale.languageName} - ${locale.tag}`;
  }
  return locale.tag;
};

export const getApprovalStatusLabel = (
  status: MetaApprovalStatusDefinition | PseudoApprovalStatusDefinition,
  isWhatsAppGAEnabled?: boolean,
) => {
  switch (status) {
    case MetaApprovalStatusDefinition.Approved:
      return isWhatsAppGAEnabled ? 'All approved' : 'Approved';
    case MetaApprovalStatusDefinition.NotSubmitted:
      return 'Draft';
    case MetaApprovalStatusDefinition.Pending:
      return 'In-review';
    case MetaApprovalStatusDefinition.FirstPaused:
      return '1st pause (3 hours)';
    case MetaApprovalStatusDefinition.SecondPaused:
      return '2nd pause (6 hours)';
    default:
      return status;
  }
};

export const getTemplateTypeLabel = (type: TemplateTypeDefinition) => {
  switch (type) {
    case TemplateTypeDefinition.BusinessInitiated:
      return 'Business-initiated';
    case TemplateTypeDefinition.UserInitiated:
      return 'User-initiated';
    default:
      return '';
  }
};

export const getQualityRatingLabel = (qualityRating: MetaQualityRating) =>
  `${qualityRating === MetaQualityRating.Unknown ? 'Pending' : qualityRating} quality`;

export const getTemplatePreviewUrl = (messageMedium: string, templateId: number) => {
  const baseUrl =
    messageMedium === IN_APP || messageMedium === EMAIL
      ? PREVIEW_HTML_TEMPLATE_URL
      : PREVIEW_NON_HTML_TEMPLATE_URL;
  return `${baseUrl}templateId=${templateId.toString()}`;
};

export const validateWhatsAppTemplateName = (name: string) => {
  const invalidCharRegex = /[^a-z0-9_]/;
  return !!name && !invalidCharRegex.test(name);
};

/** Note: "Paused" needs to be removed from the list of statuses sent to the graphql
 * query because it is only used for user interactivity and is not an actual status,
 * and it should be replaced with FirstPaused and SecondPaused statuses under the hood. */
export const replacePseudoStatus = (statuses: string[]) =>
  statuses.reduce(
    (acc: string[], status) => [
      ...acc,
      ...(status === PseudoApprovalStatusDefinition.Paused
        ? [MetaApprovalStatusDefinition.FirstPaused, MetaApprovalStatusDefinition.SecondPaused]
        : [status]),
    ],
    [],
  );
